import { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  GameState,
  TableauIndex,
  FoundationIndex,
  SelectedSource,
} from "../types";
import {
  createDeck,
  canPlayOnFoundation,
  isGameWon,
  isGameOver,
} from "../gameLogic";

const STORAGE_KEY = "calculationGameState";

// We now only persist the history, as it contains all needed game state
interface PersistentGameData {
  history: GameStateHistory[];
}

const initialGameState: GameState = {
  deck: [],
  stock: [],
  foundations: [[], [], [], []],
  tableau: [[], [], [], []],
  currentCard: null,
  gameWon: false,
  gameOver: false,
  selectedCard: null,
  selectedSource: null,
};

// Define a type for our history stack
type GameStateHistory = {
  state: GameState;
};

export const useGameState = () => {
  const [gameState, setGameState] = useState<GameState>(initialGameState);
  const [history, setHistory] = useState<GameStateHistory[]>([]);
  const initialLoadDone = useRef(false);

  // Save current state to history before making a move
  // No need for saveToHistory since we track state changes in the effect

  // Undo the last move
  const undo = useCallback((): void => {
    if (history.length <= 1) return; // Keep at least the initial history entry

    // Get the previous state (second to last entry in history)
    const previousEntry = history[history.length - 2];
    const previousState = previousEntry.state;

    // Set the undo flag to prevent the effect from adding to history
    isUndoingRef.current = true;
    
    // Update the game state to the previous state
    setGameState(previousState);

    // Temporarily disable the effect tracking while we update game state
    prevGameStateRef.current = { ...previousState };

    // Remove the last entry from history
    setHistory((prevHistory) => prevHistory.slice(0, -1));
  }, [history]);

  // Initialize a new game
  const newGame = useCallback((): void => {
    console.info("New game")
    const { deck: newDeck, foundationCards } = createDeck();

    // Set up foundations with A, 2, 3, 4
    const newFoundations: Card[][] = [[], [], [], []];
    foundationCards.forEach((card) => {
      if (card.value === "A") newFoundations[0].push(card);
      else if (card.value === "2") newFoundations[1].push(card);
      else if (card.value === "3") newFoundations[2].push(card);
      else if (card.value === "4") newFoundations[3].push(card);
    });

    // Draw the first card
    const firstCard = newDeck.length > 0 ? newDeck[0] : null;
    const restOfDeck = newDeck.slice(1);

    // Initialize with the first card already selected to simplify state transitions
    const newState: GameState = {
      deck: newDeck,
      stock: restOfDeck,
      foundations: newFoundations,
      tableau: [[], [], [], []],
      currentCard: firstCard,
      gameWon: false,
      gameOver: false,
      selectedCard: firstCard,
      selectedSource: firstCard ? "current" : null,
    };

    setGameState(newState);

    const initialHistoryEntry = {
      state: { ...newState }
    };
    
    // Set initial history
    initialLoadingRef.current = true; // Prevent auto-save during initialization
    setHistory([initialHistoryEntry]);
  }, []);

  // Get valid foundation destinations for a card
  const getValidFoundationDestinations = useCallback(
    (card: Card | null): FoundationIndex[] => {
      if (!card) return [];

      const validDestinations: FoundationIndex[] = [];
      for (let i = 0; i < 4; i++) {
        if (
          canPlayOnFoundation(card, i as FoundationIndex, gameState.foundations)
        ) {
          validDestinations.push(i as FoundationIndex);
        }
      }
      return validDestinations;
    },
    [gameState.foundations],
  );

  // Handle card selection
  const selectCard = useCallback(
    (card: Card, source: SelectedSource): void => {
      if (gameState.gameWon || gameState.gameOver) return;

      setGameState((state) => {
        // If already selected, deselect
        if (state.selectedCard && card.value === state.selectedCard.value) {
          return {
            ...state,
            selectedCard: null,
            selectedSource: null,
          };
        }

        return {
          ...state,
          selectedCard: card,
          selectedSource: source,
        };
      });
    },
    [gameState.gameWon, gameState.gameOver],
  );

  // Auto-draw card after placement
  const autoDrawNextCard = useCallback((): void => {
    if (gameState.stock.length === 0) return;

    // We don't save to history here since this is part of a move
    setGameState((state) => {
      const newStock = [...state.stock];
      const nextCard = newStock.shift();

      if (nextCard) {
        // Include selection in the same state update to prevent flickering
        return {
          ...state,
          stock: newStock,
          currentCard: nextCard,
          selectedCard: nextCard,
          selectedSource: "current",
        };
      }

      return state;
    });
  }, [gameState.stock]);

  // Move current card to foundation
  const moveCurrentToFoundation = useCallback(
    (foundationIndex: FoundationIndex): void => {
      setGameState((state) => {
        if (
          !state.currentCard ||
          !canPlayOnFoundation(
            state.currentCard,
            foundationIndex,
            state.foundations,
          )
        ) {
          return state;
        }

        const newFoundations = [...state.foundations];
        newFoundations[foundationIndex] = [
          ...newFoundations[foundationIndex],
          state.currentCard,
        ];

        return {
          ...state,
          foundations: newFoundations,
          currentCard: null,
          selectedCard: null,
          selectedSource: null,
        };
      });

      // Auto-draw next card after placement
      autoDrawNextCard();
      // History saved automatically by effect
    },
    [autoDrawNextCard],
  );

  // Move current card to tableau
  const moveCurrentToTableau = useCallback(
    (tableauIndex: TableauIndex): void => {
      setGameState((state) => {
        if (!state.currentCard) return state;

        const newTableau = [...state.tableau];
        newTableau[tableauIndex] = [
          ...newTableau[tableauIndex],
          state.currentCard,
        ];

        return {
          ...state,
          tableau: newTableau,
          currentCard: null,
          selectedCard: null,
          selectedSource: null,
        };
      });

      // Auto-draw next card after placement
      autoDrawNextCard();
      // History saved automatically by effect
    },
    [autoDrawNextCard],
  );

  // Move card from tableau to foundation
  const moveTableauToFoundation = useCallback(
    (tableauIndex: TableauIndex, foundationIndex: FoundationIndex): void => {
      // Get current state values we need to check
      const { tableau, foundations } = gameState;

      // Pre-validate that this is a valid move before doing any state updates
      if (tableau[tableauIndex].length === 0) return;

      const topCard = tableau[tableauIndex][tableau[tableauIndex].length - 1];
      if (!canPlayOnFoundation(topCard, foundationIndex, foundations)) return;

      // Perform a single state update to move the card
      setGameState((state) => {
        // Clone the state arrays we need to modify
        const newTableau = state.tableau.map((pile, idx) =>
          idx === tableauIndex ? [...pile.slice(0, -1)] : [...pile],
        );

        const newFoundations = state.foundations.map((pile, idx) =>
          idx === foundationIndex ? [...pile, topCard] : [...pile],
        );

        return {
          ...state,
          tableau: newTableau,
          foundations: newFoundations,
          selectedCard: null,
          selectedSource: null,
        };
      });

      // Don't auto-draw here - it should only happen when we place the current card
      // History saved automatically by effect
    },
    [gameState],
  );

  // Handle destination selection
  const selectDestination = useCallback(
    (type: "foundation" | "tableau", index: number): void => {
      if (!gameState.selectedCard || gameState.selectedSource === null) return;

      if (type === "foundation") {
        const validFoundations = getValidFoundationDestinations(
          gameState.selectedCard,
        );

        if (validFoundations.includes(index as FoundationIndex)) {
          if (gameState.selectedSource === "current") {
            // When moving from current card, we should auto-draw next
            moveCurrentToFoundation(index as FoundationIndex);
          } else {
            // When moving from tableau, no auto-draw should happen
            moveTableauToFoundation(
              gameState.selectedSource as TableauIndex,
              index as FoundationIndex,
            );
          }
        }
      } else if (type === "tableau") {
        if (gameState.selectedSource === "current") {
          // When moving from current card to tableau, we should auto-draw next
          moveCurrentToTableau(index as TableauIndex);
        }
      }
    },
    [
      gameState.selectedCard,
      gameState.selectedSource,
      getValidFoundationDestinations,
      moveCurrentToFoundation,
      moveTableauToFoundation,
      moveCurrentToTableau,
    ],
  );

  // Check if game is won or over
  useEffect(() => {
    const gameWon = isGameWon(gameState.foundations);
    const gameOver =
      !gameWon &&
      isGameOver(
        gameState.stock,
        gameState.currentCard,
        gameState.tableau,
        gameState.foundations,
      );

    if (gameWon !== gameState.gameWon || gameOver !== gameState.gameOver) {
      setGameState((state) => {
        return {
          ...state,
          gameWon,
          gameOver,
        };
      });
    }
  }, [
    gameState.stock,
    gameState.currentCard,
    gameState.tableau,
    gameState.foundations,
    gameState.gameWon,
    gameState.gameOver,
  ]);
  
  // Reference to track if we should add to history
  const prevGameStateRef = useRef<GameState | null>(null);
  
  // For tracking if we're currently in an undo operation
  const isUndoingRef = useRef(false);
  
  // Track significant game state changes and add to history automatically
  useEffect(() => {
    // Skip during initial load
    if (!prevGameStateRef.current) {
      prevGameStateRef.current = { ...gameState };
      return;
    }
    
    const prev = prevGameStateRef.current;
    
    // Determine if a significant game state change occurred
    const significantChange = 
      // Current card changed (comparing values since cards don't have ids)
      prev.currentCard?.value !== gameState.currentCard?.value ||
      // Foundations changed
      prev.foundations.some((pile, i) => 
        pile.length !== gameState.foundations[i].length
      ) ||
      // Tableau changed
      prev.tableau.some((pile, i) => 
        pile.length !== gameState.tableau[i].length
      );
    
    // If we just performed an undo, we don't want to add to history again
    if (isUndoingRef.current) {
      isUndoingRef.current = false;
      prevGameStateRef.current = { ...gameState };
      return;
    }
      
    // If there was a significant change
    if (significantChange) {
      // Update our ref with the new state
      prevGameStateRef.current = { ...gameState };
      
      // Add current state to history
      setHistory(prevHistory => [...prevHistory, { state: { ...gameState } }]);
    }
  }, [gameState]);
  
  // Flag to track if we're initializing from storage
  const initialLoadingRef = useRef(true);

  // Save history to localStorage whenever history changes
  useEffect(() => {
    // Skip saving during the initial load from localStorage
    if (initialLoadingRef.current) {
      initialLoadingRef.current = false;
      return;
    }

    try {
      console.info("Saved history to localStorage", history.length);
      const persistentData: PersistentGameData = {
        history: history,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persistentData));
    } catch (error) {
      console.error("Error saving game state to localStorage:", error);
    }
  }, [history]);

  // Helper function to validate loaded game data
  const isValidGameData = (data: any): data is PersistentGameData => {
    return (
      data &&
      Array.isArray(data.history) &&
      data.history.length > 0 &&
      data.history[0].state !== undefined
    );
  };

  // Initialize game on first load or load from localStorage
  useEffect(() => {
    // Try to load saved game data from localStorage
    const loadSavedGameData = (): PersistentGameData | null => {
      try {
        const savedGameState = localStorage.getItem(STORAGE_KEY);

        // No saved state or invalid string
        if (!savedGameState || savedGameState === "undefined") {
          return null;
        }

        const parsedData = JSON.parse(savedGameState);

        // Validate the data structure
        if (!isValidGameData(parsedData)) {
          return null;
        }

        return parsedData;
      } catch (error) {
        console.error("Error loading game state from localStorage:", error);
        return null;
      }
    };

    // Only load from localStorage on the first mount
    // This prevents issues with React's StrictMode double-mounting components
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    // Try to restore saved game
    const savedData = loadSavedGameData();

    if (savedData && savedData.history.length > 0) {
      console.info("Loading saved game from localStorage", savedData.history.length);
      
      // Get the most recent state from history
      const lastHistoryEntry = savedData.history[savedData.history.length - 1];
      const lastState = lastHistoryEntry.state;

      // Restore game state from history
      setGameState({
        ...initialGameState, // Reset ephemeral state
        ...lastState, // Get state from last history entry
        // Initialize selection to the current card if available
        selectedCard: lastState.currentCard,
        selectedSource: lastState.currentCard ? "current" : null,
      });

      // Important: Set history AFTER initialLoadingRef is read in the save effect
      // by delaying it slightly we ensure it doesn't trigger an immediate save
      setTimeout(() => {
        // Restore the entire history
        setHistory(savedData.history);
      }, 0);
      return;
    }

    // No saved game found or error loading, start a new game
    console.info("No saved game found, starting new game");
    newGame();
  }, [newGame]);

  return {
    gameState,
    newGame,
    selectCard,
    selectDestination,
    getValidFoundationDestinations,
    undo,
    canUndo: history.length > 0,
  };
};

export default useGameState;
